import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '../data/articles';
import { getAuthorById } from '../data/authors';
import { ArticleBody } from '../components/article/ArticleBody';
import { ArticleCTABand } from '../components/article/ArticleCTABand';
import { ArticleFAQ } from '../components/article/ArticleFAQ';
import { ArticleTOC } from '../components/article/ArticleTOC';
import { usePageMeta } from '../hooks/usePageMeta';
import { useArticleSeo } from '../hooks/useArticleSeo';
import { buildMatchLink } from '../utils/matchParams';
const FAQ_ID = 'faqs';
export function ArticlePage() {
  const {
    articleName
  } = useParams<{
    articleName: string;
  }>();
  const location = useLocation();
  // Each article also has an explicit route so it appears as its own URL, and
  // those routes carry no `:articleName` param — fall back to the path.
  const slug = articleName || location.pathname.split('/').filter(Boolean).pop() || '';
  const article = getArticleBySlug(slug);
  usePageMeta(article?.metaTitle || 'Article | FindOnlineColleges', article?.metaDescription || 'Compare accredited online degree programs. Get matched by career goals, schedule, and budget.', {
    ogType: 'article',
    ogImage: article?.image
  });
  // Canonical, article:* tags, and Article/Breadcrumb structured data.
  useArticleSeo(article, getAuthorById(article?.authorId));
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <main className="min-h-[60vh] flex items-center justify-center px-4 py-20" data-id="element-1048" data-mp="22zt3mjfhll9t">
        <div className="text-center max-w-md" data-id="element-1049" data-mp="1y7ja6tf5y32s">
          <h1 className="text-2xl font-bold text-navy mb-3" data-id="element-1050" data-mp="1y6rn66zmtdo4">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-1051" data-mp="1agdqg363qqn7">
            We couldn't find that article. Browse our guides or get matched with
            accredited programs instead.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold py-3 px-7 rounded-lg text-sm transition-colors duration-200" data-id="element-1052" data-mp="35l3ncu82smfs">
            Get Matched
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1053" data-mp="3kb2kctnw9rto" />
          </Link>
        </div>
      </main>;
  }
  const author = getAuthorById(article.authorId);
  const related = getRelatedArticles(article.slug);
  const midpoint = Math.ceil(article.sections.length / 2);
  const matchHref = buildMatchLink('/request-information', article.match);
  return <main className="bg-white" data-id="element-1054" data-mp="2wn1jm6wyjfot">
      {/* Hero */}
      <header className="hero-bg hero-bg-flipped" data-id="element-1055" data-mp="31kbcjwjk29he">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1056" data-mp="31howt9migno3">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1057" data-mp="bug623cl116m">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1058" data-mp="gt17u77hfsx6">
              <li data-id="element-1059" data-mp="21ble9fmn1c0v">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1060" data-mp="21atk898ovmcp">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1061" data-mp="1wev1b164qwjo">/</li>
              <li className="text-white/90" data-id="element-1062" data-mp="1rlibwotmmlpv">{article.category}</li>
            </ol>
          </nav>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-1063" data-mp="1mngxwib25cl9">
            {article.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-4xl" data-id="element-1064" data-mp="1hpfe1v5t598q">
            {article.title}
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mb-6" data-id="element-1065" data-mp="18mxob6qy1ygz">
            {article.intro}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/60" data-id="element-1066" data-mp="13ow4gjlp1v4g">
            {author && <a href="#about-author" className="inline-flex items-center gap-3 text-white/90 hover:text-white transition-colors duration-150" data-id="element-1067" data-mp="330ox641g8ebd">
                <img src={author.image} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-white/25" data-id="element-1068" data-mp="1vq0wa4hui2co" />
                <span className="leading-tight" data-id="element-1069" data-mp="1lpgtgxeogpsc">
                  <span className="block text-sm font-semibold" data-id="element-1070" data-mp="1lsw7dwwm7ss6">
                    By {author.name}
                  </span>
                  <span className="block text-xs text-white/60" data-id="element-1071" data-mp="1c070zgfx953p">
                    {author.role}
                  </span>
                </span>
              </a>}
            <span className="inline-flex items-center gap-1.5" data-id="element-1072" data-mp="1300q82blyj88">
              <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1073" data-mp="131llxv9ns87o" />
              Updated {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-1074" data-mp="17wr88v6z3owa">
              <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1075" data-mp="t2e713na05ig" />
              {article.readMinutes} min read
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1076" data-mp="efnx5dvhl4l9">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1077" data-mp="aelvkqe3kgg4">
          <article className="lg:w-2/3 space-y-12" data-id="element-1078" data-mp="odbsa4x08e3y">
            <ArticleBody sections={article.sections} interruptAfter={midpoint} interruption={<ArticleCTABand variant="inline" body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-1080" data-mp="jglbtghafgi0" />} data-id="element-1079" data-mp="oe4pofa2pr9f" />
            <ArticleFAQ id={FAQ_ID} faqs={article.faqs} data-id="element-1081" data-mp="172rv4yw5winj" />

            {author && <section id="about-author" aria-labelledby="about-author-heading" className="flex flex-col sm:flex-row gap-5 border-t border-gray-200 pt-8 scroll-mt-24" data-id="element-1082" data-mp="124gjcimb3y2o">
                <img src={author.image} alt={author.name} loading="lazy" className="w-20 h-20 rounded-full object-cover flex-shrink-0" data-id="element-1083" data-mp="1c4h8il6ux8es" />
                <div data-id="element-1084" data-mp="1o2unlxsvbt2k">
                  <p className="text-xs font-semibold text-gray-500 mb-1" data-id="element-1085" data-mp="1t4k0zk3kcitt">
                    About the author
                  </p>
                  <h2 id="about-author-heading" className="text-lg font-bold text-navy leading-tight" data-id="element-1086" data-mp="1xvsbykkjkc93">
                    {author.name}
                  </h2>
                  <p className="text-sm text-gray-500 mb-3" data-id="element-1087" data-mp="26tvleii95ikj">{author.role}</p>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1088" data-mp="2bvt9bq1ll1jt">
                    {author.bio}
                  </p>
                  <Link to={`/authors#${author.id}`} className="group mt-3 inline-flex items-center text-sm font-semibold text-navy hover:text-navy-dark" data-id="element-1089" data-mp="2gt383wavmpkb">
                    Meet our authors
                    <ArrowRightIcon className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1090" data-mp="ewkmw4mcqt9u" />
                  </Link>
                </div>
              </section>}
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/3 space-y-8" data-id="element-1091" data-mp="8zhu55x2jza">
            <div className="lg:sticky lg:top-24 space-y-8" data-id="element-1092" data-mp="xux0q3o7krbr">
              <div className="bg-navy rounded-xl p-6" data-id="element-1093" data-mp="xt40p783p5cq">
                <h2 className="text-lg font-bold text-white mb-2" data-id="element-1094" data-mp="svtzyvf9h79l">
                  Get Matched
                </h2>
                <p className="text-sm text-white/75 leading-relaxed mb-5" data-id="element-1095" data-mp="nvpip36j48ib">
                  Answer a few questions and we'll connect you with accredited
                  programs that fit your goals, schedule, and budget.
                </p>
                <Link to={matchHref} className="block text-center bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm transition-colors duration-200" data-id="element-1096" data-mp="33o4zjc5s2abk">
                  {article.cta.buttonLabel}
                </Link>
                <p className="mt-3 text-xs text-white/55 text-center" data-id="element-1097" data-mp="23mwg1h28hlh">
                  No cost to you. No obligation.
                </p>
              </div>

              <ArticleTOC sections={article.sections} faqId={FAQ_ID} data-id="element-1098" data-mp="3odz94a0qs0ce" />

              <div className="border-t border-gray-200 pt-6" data-id="element-1099" data-mp="fx4cvdeg9e7a">
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-4" data-id="element-1100" data-mp="fzql7f95zxqk">
                  Keep Reading
                </h2>
                <ul className="space-y-4" data-id="element-1101" data-mp="7410l8fjj7e8">
                  {article.internalLinks.map(link => <li key={link.anchor} data-id="element-1102" data-mp="b5cw1ul3imuc">
                      <Link to={buildMatchLink(link.to, link.match ?? article.match)} className="group flex items-start gap-2 text-sm text-gray-700 hover:text-navy transition-colors duration-150" data-id="element-1103" data-mp="b4b9otymqdxb">
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-150" aria-hidden="true" data-id="element-1104" data-mp="2jd9fkdyp8y5m" />
                        <span className="capitalize" data-id="element-1105" data-mp="2t6gg9lihvpzd">{link.anchor}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && <section aria-labelledby="related-heading" className="bg-light border-t border-gray-200 py-14" data-id="element-1106" data-mp="2adma9h2un87a">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1107" data-mp="3qr2znwedjbqj">
            <h2 id="related-heading" className="text-2xl font-bold text-navy mb-6" data-id="element-1108" data-mp="3lsrpfkk62z7s">
              Related Guides
            </h2>
            <ul className="grid sm:grid-cols-3 gap-5" data-id="element-1109" data-mp="2o32kijguh9xc">
              {related.map(item => <li key={item.slug} className="flex" data-id="element-1110" data-mp="2j5km3n0ged3l">
                  <Link to={`/article/${item.slug}`} className="group bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-1111" data-mp="2edapjo4u2esc">
                    <span className="block aspect-[16/9] bg-navy/10 overflow-hidden" data-id="element-1112" data-mp="24djunlcd1xrq">
                      {item.image && <img src={item.image} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-1113" data-mp="1veona5xkyhmu" />}
                    </span>
                    <span className="p-6 flex flex-col flex-1" data-id="element-1114" data-mp="3kuxs9r8xd8g3">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-1115" data-mp="3scm9g1ila0ag">
                      {item.category}
                    </span>
                    <span className="font-bold text-gray-900 leading-snug mb-3" data-id="element-1116" data-mp="5yco619yh8dw">
                      {item.title}
                    </span>
                    <span className="mt-auto inline-flex items-center text-sm font-semibold text-navy" data-id="element-1117" data-mp="avupp6ln8lbv">
                      Read guide
                      <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-1118" data-mp="39h5ubt0emuy1" />
                    </span>
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      <ArticleCTABand body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-1119" data-mp="youncr7k6pm1" />
    </main>;
}